import { Application } from "Application";
import { LocationID } from "Data/GameConfig";
import * as Topics from "Data/Topics";
import { Sine } from "gsap/EasePack";
import { TweenLite } from "gsap/TweenLite";
import * as HLF from "html-living-framework";
import { IFocusable } from "html-living-framework/lib/Input";
import { PixiUtils, RectUtils } from "html-living-framework/lib/Utils";
import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

export class CitySelectButton extends PIXI.Container implements IFocusable {
    public sprite: PIXI.Sprite;

    protected _hoverVO: string | undefined;
    protected _clickedSFX: string = "ui_select";
    protected _callback?: () => void;

    constructor(
        pFilename: string,
        pLevel?: LocationID,
        pCallback?: (locationID: LocationID) => void,
        pHoverVO?: string,
    ) {
        super();
        const sprite = makeSprite(pFilename, ["cityselect-1"]);
        sprite.anchor.copy(sprite.texture.defaultAnchor);
        this.position.set(
            (sprite.texture.defaultAnchor.x * sprite.width) - (sprite.width / 2),
            (sprite.texture.defaultAnchor.y * sprite.height) - (sprite.height / 2),
        );

        this.addChild(sprite);

        if (pCallback !== undefined && pLevel !== undefined) {

            this._callback = () => pCallback(pLevel);

            const hitArea = sprite.texture.trim!.clone();
            hitArea.x -= sprite.width * sprite.texture.defaultAnchor.x;
            hitArea.y -= sprite.height * sprite.texture.defaultAnchor.y;
            sprite.hitArea = hitArea;

            this.interactive = true;
            this.buttonMode = true;

            this.on("pointertap", () => this.onClick());
            this.on("pointerover", () => this.pointerOver());
            this.on("pointerout", () => this.pointerOut());
            this.on("pointerup", () => this.pointerUp());
            this.on("pointerupoutside", () => this.pointerOut());
            this.on("pointercancel", () => this.pointerOut());
            this.on("pointerdown", () => this.pointerDown());
        }
        this.sprite = sprite;
        this._hoverVO = pHoverVO;
    }

    public onFocusEnd(): void {
        this.pointerOut();
    }
    public getFocusPosition(): PIXI.Point {
        return this.sprite.toGlobal(RectUtils.center(this.sprite.hitArea as PIXI.Rectangle));
    }
    public getFocusSize(): PIXI.Point {
        return RectUtils.size(PixiUtils.getGlobalBounds(this, (this.sprite.hitArea as PIXI.Rectangle).clone()));
    }
    public onFocusBegin(): void {
        this.playHoverVO();
        this.pointerOver();
    }
    public onFocusActivated(): void {
        if (this.interactive) {
            this.playClickedSFX();
            PubSub.publishSync(Topics.RESTART_IDLE_TIMER, undefined);
            this._callback?.();
        }
    }
    protected playHoverVO(): void {
        if (this._hoverVO !== undefined) {
            Application.instance.voiceover.playVO(this._hoverVO, {priority: -1});
            PubSub.publishSync(Topics.RESTART_IDLE_TIMER, undefined);
        }
    }

    protected onClick() {
        this.playClickedSFX();
        PubSub.publishSync(Topics.RESTART_IDLE_TIMER, undefined);
        this._callback?.();
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            Application.instance.audio.play(this._clickedSFX, 1, false, HLF.AudioCategory.SFX);
        }
    }

    private pointerDown(): void {
        TweenLite.to(this.sprite.scale, 0.15, {
            x: 0.95,
            y: 0.95,
            ease: Sine.easeInOut,
        });
    }

    private pointerUp(): void {
        TweenLite.to(this.sprite.scale, 0.15, {
            x: 1.1,
            y: 1.1,
            ease: Sine.easeInOut,
        });
    }

    private pointerOut() {
        TweenLite.to(this.sprite.scale, 0.15, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        });
    }

    private pointerOver(): void {
        this.playHoverVO();
        TweenLite.to(this.sprite.scale, 0.15, {
            x: 1.1,
            y: 1.1,
            ease: Sine.easeInOut,
        });
    }
}
